import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ExternalLink, Link as LinkIcon, X } from 'lucide-react';
import { getFunnelNodeStats } from '@/services/api/funnel';
import type { FunnelNodeStats, StuckReason } from '@/types/funnel';
import { causeMeta } from '@/components/broadcast-details-modal/errorCatalog';
import { fmtDateTime, fmtDec, fmtInt } from '@/components/broadcast-details-modal/utils';
import { INFO, InfoTip } from './InfoTip';

export const REASONS: Record<StuckReason, { label: string; color: string; text: string }> = {
    aguardando_clique: { label: 'Não clicou', color: '#94a3b8', text: 'Recebeu a mensagem e não clicou em nenhum botão que continua o funil. É o abandono real deste passo.' },
    clicou_url: { label: 'Clicou só no link', color: '#0ea5e9', text: 'Abriu um botão de link. O link leva ao site, mas não avança o funil — o lead fica neste node mesmo tendo clicado.' },
    falhou: { label: 'Envio falhou', color: '#ef4444', text: 'A Meta recusou a mensagem deste node, então o lead nunca a viu e o funil parou aqui. Veja o erro de cada um na lista.' },
    em_delay: { label: 'Esperando delay', color: '#f59e0b', text: 'Está aguardando o tempo do delay vencer. O funil segue sozinho, não precisa de ação.' },
    fim_do_funil: { label: 'Terminou o funil', color: '#10b981', text: 'Este é o último node: o lead completou o funil. Não é problema.' },
    interrompido: { label: 'Interrompido', color: '#8b5cf6', text: 'O node tem continuação, mas o lead não seguiu: mandou outra mensagem e reiniciou o funil, o delay foi descartado ou a execução foi cortada.' },
};

const ERROR_HELP: Record<string, string> = {
    '551': 'A pessoa não aceita mensagem da página nesta conversa (bloqueou, denunciou ou a Meta fechou a conversa). Não é problema do sistema nem do app.',
    '10': 'O app não tem permissão para esse envio. Com o subcódigo 1893063 o problema é o app/token da página: trocar o app costuma resolver.',
    '190': 'Token da página inválido ou expirado. Reconecte a página neste app.',
    '2022': 'A página está com restrição temporária do Meta.',
    '613': 'Limite de envio do Meta. Some sozinho em alguns minutos.',
    '100': 'O destinatário não existe para esta página (PSID inválido) ou a mensagem está malformada.',
    '200': 'O token não tem a permissão necessária.',
    sem_codigo: 'Falha registrada antes de 18/09, quando o sistema ainda não guardava o código do erro.',
};

const REASON_ORDER: StuckReason[] = ['aguardando_clique', 'clicou_url', 'falhou', 'em_delay', 'interrompido', 'fim_do_funil'];

interface Props {
    funnelId: number;
    nodeId: number;
    start: string;
    end: string;
    pageId: string | null;
    onClose: () => void;
    onGoToNode: (nodeId: number) => void;
}

export default function NodeDetailCard({ funnelId, nodeId, start, end, pageId, onClose, onGoToNode }: Props) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['funnel-node-stats', funnelId, nodeId, start, end, pageId],
        queryFn: () => getFunnelNodeStats(funnelId, nodeId, start, end, pageId),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    return (
        <div
            className="nowheel nodrag nopan w-[400px] max-h-[72vh] flex flex-col rounded-xl border border-gray-200 bg-white shadow-2xl text-left cursor-default"
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-start gap-2 px-4 py-3 border-b border-gray-100 shrink-0">
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{data?.node.tipo ?? 'Node'}</div>
                    <div className="text-sm font-bold text-gray-900 truncate">{data?.node.nome ?? '…'}</div>
                    {pageId && <div className="text-[10px] font-semibold text-blue-700 mt-0.5">Só a página {pageId}</div>}
                </div>
                <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Fechar">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                {isError ? (
                    <div className="p-4 text-sm text-red-600">Não foi possível carregar este node.</div>
                ) : isLoading || !data ? (
                    <div className="p-4 space-y-2">
                        {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />)}
                    </div>
                ) : (
                    <Body data={data} onGoToNode={onGoToNode} />
                )}
            </div>
        </div>
    );
}

function Section({ title, hint, info, children }: { title: string; hint?: string; info?: string; children: ReactNode }) {
    return (
        <div className="px-4 py-3 border-b border-gray-100 last:border-b-0">
            <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1">
                {title}{info && <InfoTip title={title} text={info} />}
            </div>
            {hint && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{hint}</p>}
            <div className="mt-2">{children}</div>
        </div>
    );
}

function Body({ data, onGoToNode }: { data: FunnelNodeStats; onGoToNode: (nodeId: number) => void }) {
    const [reasonFilter, setReasonFilter] = useState<StuckReason | null>(null);
    const sends = data.sent + data.failed;
    const leads = useMemo(
        () => data.leads.filter((l) => !reasonFilter || l.reason === reasonFilter),
        [data.leads, reasonFilter],
    );
    const breakdownTotal = Object.values(data.stuck.breakdown).reduce((a, b) => a + (b ?? 0), 0);
    const hasSends = sends > 0;

    return (
        <>
            <Section
                title="Resumo do período"
                hint={`${data.period.start.split('-').reverse().join('/')} a ${data.period.end.split('-').reverse().join('/')}`}
            >
                <div className="grid grid-cols-3 gap-2">
                    <Stat label="Chegaram" value={fmtInt(data.reached)} hint="leads diferentes" info={INFO.chegaram} />
                    <Stat
                        label="Enviados"
                        value={fmtInt(data.sent)}
                        hint={data.reached && data.sent > data.reached ? `mensagens · ${fmtDec(data.sent / data.reached)} por lead` : 'mensagens aceitas pela Meta'}
                        info={INFO.enviadosNode}
                        cls="text-blue-700"
                    />
                    <Stat label="Falhas" value={fmtInt(data.failed)} hint={hasSends ? `${fmtDec(100 - data.success_rate)}% das tentativas` : undefined} cls="text-red-600" info={INFO.falhasNode} />
                    {data.buttons.length > 0 && (
                        <>
                            <Stat label="Cliques" value={fmtInt(data.clicks)} hint={`de ${fmtInt(data.unique_clickers)} leads`} cls="text-indigo-700" info={INFO.cliquesNode} />
                            <Stat
                                label="CTR por lead"
                                value={`${fmtDec(data.reached ? (data.unique_clickers / data.reached) * 100 : 0)}%`}
                                hint={`${fmtDec(data.ctr)}% por envio`}
                                info={INFO.ctrNode}
                                cls="text-emerald-700"
                            />
                        </>
                    )}
                    <Stat label="Parados aqui" value={fmtInt(data.stuck.total)} hint={`do período · ${fmtInt(data.stuck.total_all)} desde sempre`} cls="text-orange-700" info={INFO.paradosNode} />
                </div>
            </Section>

            {data.failed > 0 && (
                <Section title={`Falhas · ${fmtInt(data.failed)}`} info={INFO.secaoFalhas} hint="Mensagens deste node que a Meta recusou. O lead não recebe e o funil para aqui.">
                    <div className="space-y-2">
                        {data.errors.map((e) => {
                            const m = causeMeta(e.code);
                            return (
                                <div key={`${e.code}|${e.subcode}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2">
                                    <div className="flex items-center gap-2 text-[12px]">
                                        <i className="w-2 h-2 rounded-sm shrink-0" style={{ background: m.color }} />
                                        <b className="flex-1 truncate">{m.label}</b>
                                        {e.subcode && <span className="text-gray-400 font-mono text-[11px]">{e.code}|{e.subcode}</span>}
                                        <span className="font-semibold">{fmtInt(e.count)}</span>
                                    </div>
                                    <p className="text-[11px] text-gray-600 mt-1 leading-snug">{ERROR_HELP[e.code] ?? m.description}</p>
                                    {e.leads > 0 && <p className="text-[10px] text-gray-400 mt-0.5">{fmtInt(e.leads)} leads diferentes</p>}
                                </div>
                            );
                        })}
                    </div>
                </Section>
            )}

            {(data.buttons.length > 0 || data.next_nodes.length > 0 || data.is_end) && (
                <Section
                    title="Para onde o lead vai"
                    info={INFO.secaoDestino}
                    hint={data.waits_click ? 'O funil espera um clique: só os botões que levam a outro node fazem o lead avançar.' : undefined}
                >
                    <div className="space-y-1.5">
                        {data.buttons.map((b) => (
                            <div key={b.button_id} className="rounded-lg border border-gray-100 px-2 py-1.5">
                                <div className="flex items-center gap-2 text-[12px]">
                                    <span className={`text-[9px] font-bold uppercase rounded px-1 shrink-0 ${b.type === 'deleted' ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-100'}`}>{b.type === 'media' ? 'mídia' : b.type === 'deleted' ? 'excluído' : b.type}</span>
                                    <b className="truncate flex-1">{b.text}</b>
                                    <span className="text-gray-700">{fmtInt(b.clicks)}</span>
                                    <span className="text-emerald-700 font-semibold w-12 text-right">{fmtDec(b.ctr)}%</span>
                                </div>
                                <div className="text-[11px] mt-0.5 flex items-center gap-1">
                                    {b.advances && b.to_node_id ? (
                                        <button type="button" onClick={() => onGoToNode(b.to_node_id!)} className="flex items-center gap-1 text-blue-600 hover:underline">
                                            <ArrowRight className="w-3 h-3" />leva para “{b.to_node_nome ?? `node ${b.to_node_id}`}”
                                        </button>
                                    ) : b.type === 'url' ? (
                                        <span className="flex items-center gap-1 text-sky-700"><ExternalLink className="w-3 h-3" />abre o link — não avança o funil</span>
                                    ) : b.type === 'deleted' ? (
                                        <span className="text-gray-500">cliques em botões que foram apagados — seguem no total do node, mas o botão não existe mais</span>
                                    ) : b.type === 'media' ? (
                                        <span className="flex items-center gap-1 text-sky-700"><LinkIcon className="w-3 h-3" />clique na imagem/vídeo — não avança o funil</span>
                                    ) : (
                                        <span className="text-red-600">não está ligado a nenhum node — quem clica fica parado aqui</span>
                                    )}
                                    <span className="ml-auto text-gray-400">{fmtInt(b.unique_clickers)} leads</span>
                                </div>
                            </div>
                        ))}
                        {data.next_nodes.map((n) => (
                            <button key={n.node_id} type="button" onClick={() => onGoToNode(n.node_id)} className="flex items-center gap-1 text-[12px] text-blue-600 hover:underline">
                                <ArrowRight className="w-3 h-3" />segue direto para “{n.nome ?? `node ${n.node_id}`}”
                            </button>
                        ))}
                        {data.is_end && <p className="text-[12px] text-emerald-700">Último node — o funil termina aqui.</p>}
                    </div>
                </Section>
            )}

            <Section
                title={`Parados aqui · ${fmtInt(data.stuck.total)}`}
                info={INFO.secaoParados}
                hint={`Dos leads que passaram por este funil no período, quantos estão com este node como o último que alcançaram. Contando todos desde sempre são ${fmtInt(data.stuck.total_all)}. Clique num motivo para filtrar a lista.`}
            >
                {breakdownTotal === 0 ? (
                    <p className="text-[12px] text-gray-400">Nenhum lead parado aqui.</p>
                ) : (
                    <div className="space-y-1.5">
                        {REASON_ORDER.filter((r) => data.stuck.breakdown[r]).map((r) => {
                            const n = data.stuck.breakdown[r] ?? 0;
                            const on = reasonFilter === r;
                            return (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => setReasonFilter(on ? null : r)}
                                    className={`w-full text-left rounded-lg border px-2 py-1.5 ${on ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:border-gray-300'}`}
                                >
                                    <div className="flex items-center gap-2 text-[12px]">
                                        <i className="w-2 h-2 rounded-sm shrink-0" style={{ background: REASONS[r].color }} />
                                        <b className="flex-1">{REASONS[r].label}</b>
                                        <span className="font-semibold">{fmtInt(n)}</span>
                                        <span className="text-gray-400 w-10 text-right">{fmtDec((n / breakdownTotal) * 100)}%</span>
                                    </div>
                                    <div className="mt-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                                        <span className="block h-full" style={{ width: `${(n / breakdownTotal) * 100}%`, background: REASONS[r].color }} />
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-1 leading-snug">{REASONS[r].text}</p>
                                </button>
                            );
                        })}
                    </div>
                )}
            </Section>

            {data.leads.length > 0 && (
                <Section
                    info={INFO.secaoLeads}
                    title={`Quem está aqui · ${reasonFilter ? `${fmtInt(leads.length)} de ` : ''}${fmtInt(data.leads.length)}`}
                    hint={data.stuck.total > data.leads.length ? `Mostrando os ${fmtInt(data.leads.length)} mais recentes de ${fmtInt(data.stuck.total)}.` : undefined}
                >
                    <div className="divide-y divide-gray-100 -mx-1">
                        {leads.map((l) => (
                            <div key={l.lead_id} className="px-1 py-1.5 text-[12px]">
                                <div className="flex items-center gap-2">
                                    <i className="w-2 h-2 rounded-full shrink-0" style={{ background: REASONS[l.reason].color }} title={REASONS[l.reason].label} />
                                    <b className="truncate flex-1">{l.nome ?? `PSID ${l.sender_id}`}</b>
                                    {!l.is_active && <span className="text-[9px] font-bold text-gray-500 bg-gray-100 rounded px-1">INATIVO</span>}
                                    <span className="text-[10px] text-gray-400">#{l.lead_id}</span>
                                </div>
                                <div className="pl-4 text-[11px] text-gray-500 flex flex-wrap gap-x-2">
                                    <span className="truncate max-w-[180px]">{l.page_name ?? l.page_id}</span>
                                    <span>{REASONS[l.reason].label}</span>
                                    {l.error_code && <span className="text-red-600">{causeMeta(l.error_code).label}{l.error_subcode ? ` · ${l.error_subcode}` : ''}</span>}
                                    {l.delay_until && <span className="text-amber-700">segue {fmtDateTime(l.delay_until)}</span>}
                                    {l.last_attempt_at && <span>envio {fmtDateTime(l.last_attempt_at)}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
        </>
    );
}

function Stat({ label, value, hint, info, cls = 'text-gray-900' }: { label: string; value: string; hint?: string; info?: string; cls?: string }) {
    return (
        <div className="rounded-lg bg-gray-50 px-2 py-1.5">
            <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1">
                {label}{info && <InfoTip title={label} text={info} />}
            </div>
            <div className={`text-[15px] font-bold leading-tight ${cls}`}>{value}</div>
            {hint && <div className="text-[10px] text-gray-400 leading-tight">{hint}</div>}
        </div>
    );
}
